import type { ReactNode } from 'react';
import type { SavedViewsListResponse } from '#services/api/saved-views.ts';
import type { getParsedSearchParams } from '../Home.utils.ts';

import { styled } from '@mend/mui';
import { useQuery } from '@tanstack/react-query';

import { providerDirectory } from '#lib/react-query/queries.ts';
import { AppointmentTypes } from './AppointmentTypes.tsx';
import { Provider } from './Provider.tsx';
import { useCoordinateAutoView } from './use-coordinate-auto-view.ts';

// Unlike Scheduling/Forms' four-column bar, this tab has exactly three controls — they share the
// full row on desktop instead of leaving a trailing gap.
const CoordinateFiltersGrid = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

/**
 * The Coordinate tab's filter bar: the Provider Filter and the Appointment Type Filter (specific
 * and base types), all multi-select, URL-backed like the rest of Signal's filters — plus the
 * auto-view sync that remembers the last-used selection per user. The memory *applies* upstream,
 * as a `?view=` token the loader resolves before this ever renders, so the first render is
 * already the remembered slice. No date range, no saved-view UI. Must render inside a
 * `FiltersProvider` with `tabType: 'coordinate'`.
 *
 * `children` is the tab content the filters scope.
 */
export function CoordinateFilters(props: {
  parsed: ReturnType<typeof getParsedSearchParams>;
  savedViewsData: SavedViewsListResponse;
  children: ReactNode;
}) {
  const providersQuery = useQuery(providerDirectory);

  useCoordinateAutoView({
    parsed: props.parsed,
    providers: providersQuery.data,
    savedViewsData: props.savedViewsData,
  });

  return (
    <>
      <CoordinateFiltersGrid>
        <Provider defaultValue={props.parsed.providerIds} />
        <AppointmentTypes
          defaultValue={{
            baseAppointmentTypes: props.parsed.baseAppointmentTypes,
            appointmentTypeIds: props.parsed.appointmentTypeIds,
          }}
        />
      </CoordinateFiltersGrid>

      {props.children}
    </>
  );
}
